
import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiTarget, FiSearch, FiUserCheck, FiArrowRight} = FiIcons;

const ProcessSection = () => {
  const processSteps = [
    {
      icon: FiTarget,
      title: 'Align',
      description: 'Deep consultation to understand your technical requirements, team culture, and hiring timeline.',
      details: ['Technical skill assessment', 'Cultural fit requirements', 'Timeline & budget planning']
    },
    {
      icon: FiSearch,
      title: 'Source',
      description: 'Targeted sourcing from our network of robotics and AI professionals using proven methodologies.',
      details: ['Network activation', 'Direct outreach', 'Technical screening']
    },
    {
      icon: FiUserCheck,
      title: 'Hire',
      description: 'Present qualified candidates, coordinate interviews, and support through offer negotiation.',
      details: ['Candidate presentation', 'Interview coordination', 'Offer negotiation']
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl font-bold text-brand-purple mb-4">
            Our Proven 3-Step Process
          </h2>
          <p className="text-xl text-brand-blue max-w-3xl mx-auto">
            From initial consultation to successful hire, we guide you through every step of building your dream team.
          </p>
        </motion.div>

        <div className="relative">
          {/* Process Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="relative"
              >
                {/* Step Card */}
                <div className="bg-white border-2 border-gray-100 rounded-xl p-8 hover:border-brand-lightblue transition-colors duration-300 relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-brand-blue to-brand-lightblue rounded-xl flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={step.icon} className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-brand-purple mb-4 text-center">
                    {step.title}
                  </h3>
                  <p className="text-brand-blue mb-6 text-center">
                    {step.description}
                  </p>
                  <ul className="space-y-2">
                    {step.details.map((detail, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-600">
                        <div className="w-2 h-2 bg-brand-orange rounded-full mr-3 flex-shrink-0"></div>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Arrow (hidden on mobile, shown on desktop between steps) */}
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
                    <div className="w-8 h-8 bg-brand-orange rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiArrowRight} className="h-4 w-4 text-white" />
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Timeline on mobile */}
          <div className="md:hidden mt-8">
            <div className="flex justify-center">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-brand-orange rounded-full"></div>
                <div className="w-8 h-0.5 bg-gray-300"></div>
                <div className="w-3 h-3 bg-brand-orange rounded-full"></div>
                <div className="w-8 h-0.5 bg-gray-300"></div>
                <div className="w-3 h-3 bg-brand-orange rounded-full"></div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <p className="text-lg text-brand-blue mb-6">
            Ready to start building your team?
          </p>
          <a
            href="https://tidycal.com/trbe/exploration-call"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-brand-orange text-white px-8 py-3 rounded-lg font-semibold hover:bg-secondary-500 transition-colors duration-200"
          >
            Book a Call Today
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessSection;
